import { AdvancedMarker } from '@vis.gl/react-google-maps';
import { useTranslation } from 'react-i18next';
import { useChargeSessionsStore } from '../store/charge-sessions-store';
import type {
  SessionCluster,
  SessionLeaf,
} from '../types/charge-session.types';

interface ChargeSessionMarkersProps {
  clusters: SessionCluster[];
  leaves: SessionLeaf[];
  /** Map host callback: zoom/expand the map onto a clicked cluster. */
  onClusterClick: (cluster: SessionCluster) => void;
  isLocked?: boolean;
}

/**
 * Bubble size tier from a cluster's active-session count. Larger bubbles read as
 * busier sites; purely visual (the count text is always shown). Colour does NOT
 * vary by tier — every cluster is the translucent-white treatment so it stays
 * visually distinct from the solid-green session leaves at any size.
 */
function clusterSizeClassName(activeSessionCount: number): string {
  if (activeSessionCount >= 50) {
    return 'h-12 min-w-12 text-sm';
  }
  if (activeSessionCount >= 10) {
    return 'h-10 min-w-10 text-sm';
  }
  return 'h-8 min-w-8 text-xs';
}

/**
 * Renders the server-clustered charge-session layer (BIZOPS-1588, contract v2).
 *
 * Clustering is done server-side (H3 in the gateway), so this component does NO
 * client-side coincident-dedup — it renders exactly what the parent pushed:
 *   - `clusters` as pulsating, translucent-WHITE count bubbles whose size scales
 *     with their active-session count; clicking a cluster asks the map host to
 *     zoom in, which triggers a fresh viewport request and finer clusters/leaves.
 *   - `leaves` as the individual pulsating, solid-GREEN point markers; clicking a
 *     leaf marks it selected in the store and the detail panel then asks the
 *     parent for Tier-2 detail (the postMessage bridge is owned by `AppInitializer`).
 *
 * Clusters and leaves are distinguished purely by COLOUR — translucent white vs
 * solid green — not shape; both are circular count bubbles and both pulse.
 */
export function ChargeSessionMarkers({
  clusters,
  leaves,
  onClusterClick,
  isLocked = false,
}: ChargeSessionMarkersProps) {
  const { t } = useTranslation();
  const selectPoint = useChargeSessionsStore((s) => s.selectPoint);
  const selectedPointId = useChargeSessionsStore((s) => s.selectedPointId);

  return (
    <>
      {clusters.map((cluster) => {
        const countLabel = t('chargeSessions.activeSessions', {
          count: cluster.activeSessionCount,
        });
        const sizeClassName = clusterSizeClassName(cluster.activeSessionCount);
        return (
          <AdvancedMarker
            key={cluster.id}
            position={{ lat: cluster.lat, lng: cluster.lng }}
            onClick={() => {
              if (isLocked) {
                return;
              }
              onClusterClick(cluster);
            }}
          >
            <div
              className="relative flex items-center justify-center"
              role="button"
              aria-label={countLabel}
              data-testid={`charge-session-cluster-${cluster.id}`}
            >
              {/* Pulsing halo — tinted grey so it stays visible on the light
                  "silver" map (a pure-white ping would vanish against it). */}
              <div className="absolute inset-0 rounded-full bg-monta-gray-400 animate-ping opacity-60" />

              {/* Translucent-white bubble with the total active-session count.
                  Dark count text + grey border/shadow keep it legible on light. */}
              <div
                className={`relative flex items-center justify-center rounded-full border-2 border-monta-gray-300 bg-white/80 px-2 font-semibold text-monta-gray-900 shadow-lg backdrop-blur-sm ${sizeClassName}`}
              >
                {cluster.activeSessionCount}
              </div>
            </div>
          </AdvancedMarker>
        );
      })}

      {leaves.map((leaf) => {
        const isSelected = selectedPointId === leaf.id;
        const countLabel = t('chargeSessions.activeSessions', {
          count: leaf.activeSessionCount,
        });
        return (
          <AdvancedMarker
            key={leaf.id}
            position={{ lat: leaf.lat, lng: leaf.lng }}
            zIndex={isSelected ? 1000 : undefined}
            onClick={() => {
              if (isLocked) {
                return;
              }
              selectPoint(leaf.id);
            }}
          >
            <div
              className="relative flex items-center justify-center"
              role="button"
              aria-label={countLabel}
              data-testid={`charge-session-marker-${leaf.id}`}
            >
              {/* Pulsing halo (shared with CenterLocationMarker) */}
              <div className="absolute inset-0 rounded-full bg-green-500 animate-ping opacity-75" />

              {/* Solid-green bubble with the active-session count */}
              <div className="relative flex h-7 min-w-7 items-center justify-center rounded-full border-2 border-white bg-green-600 px-2 text-xs font-semibold text-white shadow-lg">
                {leaf.activeSessionCount}
              </div>
            </div>
          </AdvancedMarker>
        );
      })}
    </>
  );
}
